// Google Play Billing helper for the Trusted Web Activity (Android app) build.
//
// On the open web this code is inert (isPlayBillingAvailable() === false) and the
// app keeps using Stripe. Inside the Android wrapper, the Digital Goods API +
// Payment Request API are available, so we launch Google's native purchase sheet
// and verify the purchase against our backend (/api/play/verify).

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAY_BILLING_METHOD = "https://play.google.com/billing";
const DEFAULT_PRODUCT_ID = "pro_monthly";

// True only when running inside the TWA where Play Billing is wired up.
export function isPlayBillingAvailable() {
  return (
    typeof window !== "undefined" &&
    "getDigitalGoodsService" in window &&
    typeof window.PaymentRequest === "function"
  );
}

// Launch Google's purchase flow for the Pro subscription, then verify server-side.
// Returns the backend verification result, e.g. { pro: true, status, expires_at, email }.
export async function buyProSubscription({ email, productId = DEFAULT_PRODUCT_ID }) {
  if (!isPlayBillingAvailable()) {
    throw new Error("Google Play billing isn't available in this context.");
  }
  if (!email) {
    throw new Error("An email is required to unlock Pro.");
  }

  const service = await window.getDigitalGoodsService(PLAY_BILLING_METHOD);
  const details = await service.getDetails([productId]);
  if (!details || details.length === 0) {
    throw new Error("The Pro subscription isn't available yet. Please try again shortly.");
  }
  const item = details[0];

  const methodData = [
    { supportedMethods: PLAY_BILLING_METHOD, data: { sku: item.itemId } },
  ];
  const paymentDetails = {
    total: {
      label: item.title || "PopUp Profit Pro",
      amount: {
        currency: (item.price && item.price.currency) || "USD",
        value: (item.price && item.price.value) || "9.00",
      },
    },
  };

  const request = new PaymentRequest(methodData, paymentDetails);
  const response = await request.show();

  let purchaseToken;
  try {
    purchaseToken = response.details && response.details.purchaseToken;
    if (!purchaseToken) {
      await response.complete("fail");
      throw new Error("No purchase token returned by Google Play.");
    }

    const verifyRes = await fetch(`${API}/play/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ purchaseToken, productId, email }),
    });
    const data = await verifyRes.json().catch(() => ({}));

    if (!verifyRes.ok || !data.pro) {
      await response.complete("fail");
      throw new Error((data && data.detail) || "We couldn't confirm your purchase.");
    }

    await response.complete("success");
    return data;
  } catch (err) {
    // Make sure the payment sheet is always closed out.
    try {
      await response.complete("fail");
    } catch (_) {
      /* already completed */
    }
    throw err;
  }
}

// Lightweight status check used alongside the Stripe check in lib/pro.js.
export async function getPlayProStatus(email) {
  if (!email) return { pro: false };
  try {
    const res = await fetch(`${API}/play/status?email=${encodeURIComponent(email)}`);
    return await res.json();
  } catch {
    return { pro: false };
  }
}
